<template>
  <div class="frisbuy-story-slider">
    <transition-group
      name="slide-switch"
      :duration="{ leave: 500 }"
      @after-leave="stopSlide"
      tag="span"
    >
      <template v-for="storyItem in stories" :key="storyItem.frisbuyPost.id">
        <story-slide
          v-show="storyItem.frisbuyPost.id === story?.frisbuyPost?.id"
          ref="slides"
          :class="{
            'frisbuy-story-slide--current':
              storyItem.frisbuyPost.id === story?.frisbuyPost?.id,
          }"
          :slide="storyItem"
          :isSelectedSlide="storyItem.frisbuyPost.id === story?.frisbuyPost?.id"
          :isShowNavigation="isShowNavigation"
          :isShowNavigationPrev="isShowNavigationPrev"
          :isShowNavigationNext="isShowNavigationNext"
          @pauseSlide="() => pause()"
          @playSlide="() => play()"
          @nextSlide="$emit('nextSlide')"
          @prevSlide="$emit('prevSlide')"
          @navigationTouchPause="touchPause()"
          @getCountOfActiveQueries="(count) => (hasActiveQuery = count > 0)"
          @getVideoDuration="(duration) => setTransition(duration)"
        />
      </template>
    </transition-group>
  </div>
</template>

<script>
import StorySlide from "./storySlide.vue";
import { toRaw } from "vue";

export default {
  components: {
    StorySlide,
  },

  props: [
    "stories",
    "story",
    "openedGoods",
    "isShowNavigation",
    "isShowNavigationPrev",
    "isShowNavigationNext",
  ],

  emits: [
    "nextSlide",
    "prevSlide",
    "getSlideProgress",
    "playSlide",
    "pauseSlide",
  ],

  data() {
    return {
      stepTransition: 0,
      progressTransition: 0,
      progressTimeout: null,
      isPause: false,

      hasActiveQuery: false,
    };
  },

  computed: {
    currentSlide() {
      const { story, $refs } = this;
      const { slides } = $refs;

      if (!slides || !slides.length) return null;

      const [firstSlide] = slides;

      const result =
        slides.find((slide) => slide.slide === toRaw(story)) || firstSlide;
      return result;
    },

    nextSlide() {
      const { slides } = this.$refs;

      const currentIndex = slides.indexOf(this.currentSlide);

      return slides[currentIndex + 1];
    },
  },

  watch: {
    hasActiveQuery(value) {
      if (value) {
        if (!this.isPause) {
          this.pause();
        }
      } else {
        setTimeout(() => {
          if (!this.hasActiveQuery && this.isPause) {
            this.play();
          }
        }, 2000);
      }
    },

    story: {
      handler() {
        this.progressTransition = 0;
      },

      immediate: true,
    },
  },

  mounted() {
    const { currentSlide } = this;
    if (currentSlide) {
      currentSlide.load();
    }
  },

  methods: {
    runTransition() {
      this.sourceInLoading = false;

      let progress = this.progressTransition + this.stepTransition;

      if (progress >= 100) {
        progress = 100;
      }

      this.$emit("getSlideProgress", progress);
      this.progressTransition = progress;

      clearTimeout(this.progressTimeout);

      this.progressTimeout = setTimeout(() => {
        if (this.progressTransition >= 100) {
          this.$emit("nextSlide");
        } else {
          this.runTransition();
        }
      }, 1000);
    },

    setTransition(duration) {
      this.stepTransition = 100 / (duration || 5);
      this.progressTransition = 0;
      this.$emit("getSlideProgress", this.progressTransition);

      setTimeout(() => {
        this.runTransition();
      }, 0);
    },

    async play() {
      await this.$nextTick();

      const video = this.currentSlide.$refs.video;

      if (this.isPause) {
        if (video) {
          let videoProgress = (video.currentTime / video.duration) * 100;
          if (isNaN(videoProgress)) {
            videoProgress = 0;
          }
          this.progressTransition = videoProgress;
        } else {
          let slideImageProgress =
            this.progressTransition - this.stepTransition;
          this.progressTransition =
            slideImageProgress >= 0 ? slideImageProgress : 0;
        }

        this.isPause = false;
        this.$emit("playSlide");

        this.currentSlide.play();
        this.runTransition();

        return;
      }

      if (this.nextSlide) {
        if (!this.nextSlide.contentIsLoaded) {
          this.nextSlide.load();
        }
      }

      try {
        this.currentSlide.play(this.setTransition);
      } catch (e) {
        console.log("Error", e.message);
      }
    },

    // NOTE: used in StoriesPreview.vue via `this.$refs.slider.stop()`
    // TODO: refactor this usage
    stop() {
      this.currentSlide.stop();
      this.progressTransition = 0;
      clearTimeout(this.progressTimeout);
      this.isPause = false;
    },

    pause() {
      clearTimeout(this.progressTimeout);
      this.currentSlide.pause();
      this.isPause = true;

      this.$emit("pauseSlide");
    },

    stopSlide(slideEl) {
      /*
        [WARNING]
        This funcs call only from 
          `transition-group after-leave="stopSlide"`
        const slide always is `undefined`
        This func is really needed?
      */
      const { slides } = this.$refs;

      const slide = slides.find((slide) => slide.$el === slideEl);

      // let slide = undefined;

      // if (slideEl) {
      //   slide = slides.find((slide) => slide.$el === slideEl);
      // } else {
      //   const slideEl = document.querySelector(".frisbuy-story-slide--current");
      //   console.log(slides[0].$el, slideEl, slides[0].$el === slideEl);

      //   slide = slideEl;
      // }

      if (!slide) return;

      slide.stop();
    },

    touchPause() {
      const playHandler = () => {
        const { openedGoods } = this;

        if (openedGoods) return;

        this.play();

        document.removeEventListener("mouseup", playHandler);
        document.removeEventListener("touchend", playHandler);
      };

      document.addEventListener("mouseup", playHandler);
      document.addEventListener("touchend", playHandler);

      this.pause();
    },
  },
};
</script>

<style lang="scss">
.frisbuy-story-slider {
  width: 100%;
  height: 100%;

  .frisbuy-story-slide {
    width: 100%;
    height: 100%;
    background-position: center;
    background-size: contain;
    background-repeat: no-repeat;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
  }
}
</style>
